import { type PayloadAction, createSlice } from '@reduxjs/toolkit';
import { closeFilesMigrationStatusKey } from 'reactApp/modules/filesMigration/filesMigration.constants';
import {
    type FilesMigrationProfileResponse,
    type FilesMigrationUpdateState,
    FilesMigrationStatus,
} from 'reactApp/modules/filesMigration/filesMigration.types';
import { safetyPersonalLocalStorage } from 'reactApp/storage';

interface FilesMigrationState {
    allChatsCount: number;
    migratedChatsCount: number;
    status: FilesMigrationStatus | null;
    migratedBytes: number;

    isLoading: boolean;
    isLoaded: boolean;

    isStatusClosed: boolean;
}

const checkIfStatusClosed = () => {
    const localStorageValue = safetyPersonalLocalStorage.getItem(closeFilesMigrationStatusKey, {
        default: () => 'opened',
    });

    return localStorageValue !== 'opened';
};

const initialState: FilesMigrationState = {
    allChatsCount: 0,
    migratedChatsCount: 0,
    status: null,
    migratedBytes: 0,
    isLoading: false,
    isLoaded: false,
    isStatusClosed: checkIfStatusClosed(),
};

const filesMigrationSlice = createSlice({
    name: 'filesMigration',
    initialState,
    reducers: {
        updateMigrationState: (state, action: PayloadAction<FilesMigrationUpdateState>) => {
            const { payload } = action;

            state.allChatsCount = payload.allChatsCount;
            state.migratedChatsCount = payload.migratedChatsCount;
            state.status = payload.status;
            state.migratedBytes = payload.migratedBytes;
        },
        handleProfileData: (state, action: PayloadAction<Partial<FilesMigrationProfileResponse>>) => {
            const { all_chats_count, migrated_count, migration_status, migration_current_size } = action.payload;

            state.allChatsCount = all_chats_count ?? 0;
            state.migratedChatsCount = migrated_count ?? 0;
            state.status = migration_status || migration_status === 0 ? (migration_status as FilesMigrationStatus) : null;
            state.migratedBytes = migration_current_size ?? 0;
        },
        cancelFilesMigrationRequest: (state) => {
            state.isLoading = true;
            state.isLoaded = false;
        },
        cancelFilesMigraionSuccess: (state) => {
            state.status = FilesMigrationStatus.cancelInProgress;

            state.isLoading = false;
            state.isLoaded = true;
        },
        cancelFilesMigraionFail: (state) => {
            state.isLoaded = false;
            state.isLoading = false;
        },
        changeStatusVisibility: (state, action: PayloadAction<{ closed: boolean }>) => {
            state.isStatusClosed = action.payload.closed;
        },
    },
});

export const {
    updateMigrationState,
    handleProfileData,
    cancelFilesMigrationRequest,
    cancelFilesMigraionSuccess,
    cancelFilesMigraionFail,
    changeStatusVisibility,
} = filesMigrationSlice.actions;
export const { reducer: filesMigrationReducer } = filesMigrationSlice;
